import React, { useCallback, useEffect, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Linking,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ParentStackParamList } from "../../navigation/types";
import { useAuthStore } from "../../state/authStore";
import {
  PlanInterval,
  subscriptionsApi,
  SubscriptionState,
} from "../../api/subscriptions";
import { colors, fontFamily, radii } from "../../theme";
import { Check } from "lucide-react-native";

// Mirrors the web Plan tab — current plan banner, plan-switch CTAs that
// respect monthly ↔ annual, two-CTA picker for first-time subscribers.
// Stripe Checkout / Portal URLs are opened via Linking; the parent
// completes payment on the hosted page and the webhook updates our DB.

type Props = NativeStackScreenProps<ParentStackParamList, "Subscription">;

const FEATURES = [
  "Unlimited cartoons",
  "No ads, ever",
  "Up to 4 kid profiles",
  "Weekly recap emails",
  "Bedtime stories",
  "Offline downloads",
];

// Apple App Store / Google Play require native in-app purchase (StoreKit /
// Play Billing) to SELL digital subscriptions inside the app — opening Stripe
// checkout/portal in-app gets the build rejected at review. Until native IAP
// is built, the mobile app is "web-only": it shows subscription STATUS but
// never initiates payment. Existing subscribers manage their plan on the web.
// Flip to `true` only after a real IAP integration lands.
const IN_APP_PURCHASE_ENABLED = false;

export default function SubscriptionScreen({ navigation }: Props) {
  const { accessToken } = useAuthStore();
  const [sub, setSub] = useState<SubscriptionState | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<PlanInterval | "manage" | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const s = await subscriptionsApi.me(accessToken);
      setSub(s);
    } catch {
      // Stay on the inactive state if /me fails — the inactive CTAs are
      // always safe to surface.
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isActive = !!sub?.isActive;
  const status = sub?.status ?? "inactive";
  const plan = sub?.plan ?? null;

  const headline = (() => {
    // TEST PRICING — flip back to "$95.88/year" / "$9.99/month" once verified.
    const price =
      plan === "annual"
        ? "$0.96/year"
        : plan === "monthly"
          ? "$0.10/month"
          : null;
    if (status === "active")
      return `Kidly+ Family${price ? ` · ${price}` : ""}`;
    if (status === "trialing")
      return `Kidly+ Family · Free trial${plan === "annual" ? " (annual)" : ""}`;
    if (status === "canceled") return "Kidly+ · Access ending soon";
    if (status === "past_due") return "Kidly+ · Payment failed";
    return "Free plan";
  })();

  const sublineText = (() => {
    if (status === "trialing" && sub?.trialEndsAt)
      return `Trial ends ${formatDate(sub.trialEndsAt)}`;
    if (status === "active" && sub?.renewsAt)
      return `Renews ${formatDate(sub.renewsAt)}`;
    if (status === "canceled" && sub?.renewsAt)
      return `Access until ${formatDate(sub.renewsAt)}`;
    if (status === "past_due")
      return "There's a problem with your membership — check your account.";
    return "Kidly+ unlocks the full catalog.";
  })();

  // Open Stripe Checkout for the given plan. Linking.openURL fires the
  // default browser on iOS/Android; the parent comes back to the app
  // afterward. Webhook will have updated `subscription.status` by then
  // so the next /me poll reflects the new state.
  const handleSubscribe = useCallback(
    async (target: PlanInterval) => {
      if (!accessToken) return;
      setActing(target);
      try {
        const { url } = await subscriptionsApi.checkout(accessToken, target);
        await Linking.openURL(url);
      } catch (e) {
        Alert.alert(
          "Couldn't start checkout",
          e instanceof Error ? e.message : "Try again in a moment.",
        );
      } finally {
        setActing(null);
      }
    },
    [accessToken],
  );

  const handleSwitchPlan = useCallback(
    async (target: PlanInterval) => {
      if (!accessToken) return;
      setActing(target);
      setSwitchError(null);
      try {
        const updated = await subscriptionsApi.switchPlan(target, accessToken);
        setSub(updated);
      } catch (e) {
        setSwitchError(
          e instanceof Error ? e.message : "Couldn't switch plan.",
        );
      } finally {
        setActing(null);
      }
    },
    [accessToken],
  );

  const handleManage = useCallback(async () => {
    if (!accessToken) return;
    setActing("manage");
    try {
      const { url } = await subscriptionsApi.portal(accessToken);
      await Linking.openURL(url);
    } catch (e) {
      Alert.alert(
        "Couldn't open billing portal",
        e instanceof Error ? e.message : "Try again in a moment.",
      );
    } finally {
      setActing(null);
    }
  }, [accessToken]);

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <TouchableOpacity
        onPress={() => navigation.goBack()}
        style={styles.back}
        hitSlop={{ top: 12, right: 12, bottom: 12, left: 12 }}
      >
        <Text style={styles.backText}>‹ Back</Text>
      </TouchableOpacity>

      {/* Status banner — gradient-ish background when active, plain when not. */}
      <View
        style={[styles.banner, isActive ? styles.bannerActive : styles.bannerIdle]}
      >
        <Text style={styles.bannerEyebrow}>YOUR PLAN</Text>
        <Text style={styles.bannerTitle}>{headline}</Text>
        <Text style={styles.bannerSub}>{sublineText}</Text>

        {loading ? (
          <ActivityIndicator
            style={{ marginTop: 18 }}
            color={colors.primaryInk}
          />
        ) : isActive ? (
          !IN_APP_PURCHASE_ENABLED ? (
            <Text style={styles.manageNote}>
              Your Kidly+ membership is active across all your devices.
            </Text>
          ) : (
          <View style={styles.ctaCol}>
            {plan === "monthly" && (
              <TouchableOpacity
                style={styles.btn}
                disabled={acting !== null}
                onPress={() => handleSwitchPlan("annual")}
              >
                <Text style={styles.btnText}>
                  {acting === "annual"
                    ? "Switching…"
                    : "Switch to annual · save 20%"}
                </Text>
              </TouchableOpacity>
            )}
            {plan === "annual" && (
              <TouchableOpacity
                style={[styles.btn, styles.btnGhost]}
                disabled={acting !== null}
                onPress={() => handleSwitchPlan("monthly")}
              >
                <Text style={styles.btnGhostText}>
                  {acting === "monthly" ? "Switching…" : "Switch to monthly"}
                </Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[styles.btn, styles.btnGhost]}
              disabled={acting !== null}
              onPress={handleManage}
            >
              <Text style={styles.btnGhostText}>
                {acting === "manage" ? "Opening…" : "Manage subscription"}
              </Text>
            </TouchableOpacity>
          </View>
          )
        ) : !IN_APP_PURCHASE_ENABLED ? (
          <Text style={styles.manageNote}>
            Kidly+ membership unlocks everything below. If your family already
            has Kidly+, it activates here automatically when you sign in.
          </Text>
        ) : (
          <View style={styles.ctaCol}>
            <TouchableOpacity
              style={styles.btn}
              disabled={acting !== null}
              onPress={() => handleSubscribe("annual")}
            >
              <Text style={styles.btnText}>
                {acting === "annual"
                  ? "Opening…"
                  : "Try Kidly+ · annual (save 20%)"}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.btn, styles.btnGhost]}
              disabled={acting !== null}
              onPress={() => handleSubscribe("monthly")}
            >
              <Text style={styles.btnGhostText}>
                {acting === "monthly" ? "Opening…" : "Or monthly · $0.10/mo"}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {switchError && <Text style={styles.errText}>{switchError}</Text>}
      </View>

      <Text style={styles.sectionTitle}>What&apos;s included</Text>
      <View style={styles.features}>
        {FEATURES.map((f) => (
          <View key={f} style={styles.featureRow}>
            <Check size={16} color={colors.primary} strokeWidth={2.5} />
            <Text style={styles.feature}>{f}</Text>
          </View>
        ))}
      </View>

      <Text style={styles.note}>No ads, ever · cancel anytime.</Text>
    </ScrollView>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: colors.bg,
    paddingHorizontal: 18,
    paddingTop: 56,
    paddingBottom: 32,
  },
  back: { alignSelf: "flex-start", marginBottom: 16 },
  backText: {
    fontFamily,
    color: colors.primaryInk,
    fontSize: 15,
    fontWeight: "800",
  },
  banner: {
    borderRadius: radii.lg,
    padding: 22,
    marginBottom: 26,
    borderWidth: 1,
  },
  bannerActive: {
    backgroundColor: "#FFE2C2",
    borderColor: "transparent",
  },
  bannerIdle: {
    backgroundColor: colors.surface,
    borderColor: colors.line,
  },
  bannerEyebrow: {
    fontFamily,
    fontSize: 11,
    fontWeight: "900",
    letterSpacing: 1.4,
    color: colors.primaryInk,
    marginBottom: 4,
  },
  bannerTitle: {
    fontFamily,
    fontSize: 22,
    fontWeight: "900",
    color: colors.ink,
    letterSpacing: -0.2,
    marginBottom: 6,
  },
  bannerSub: {
    fontFamily,
    fontSize: 14,
    fontWeight: "600",
    color: colors.ink2,
    marginBottom: 14,
  },
  ctaCol: {
    flexDirection: "column",
    gap: 10,
  },
  manageNote: {
    fontFamily,
    fontSize: 13.5,
    fontWeight: "700",
    color: colors.ink2,
    lineHeight: 19,
    marginTop: 4,
  },
  btn: {
    backgroundColor: colors.primary,
    borderRadius: radii.md,
    paddingVertical: 13,
    paddingHorizontal: 16,
    alignItems: "center",
  },
  btnGhost: {
    backgroundColor: "rgba(255,255,255,0.7)",
    borderWidth: 1.5,
    borderColor: colors.line,
  },
  btnText: {
    fontFamily,
    color: "#fff",
    fontSize: 14,
    fontWeight: "900",
  },
  btnGhostText: {
    fontFamily,
    color: colors.ink,
    fontSize: 14,
    fontWeight: "900",
  },
  errText: {
    fontFamily,
    marginTop: 12,
    fontSize: 12.5,
    fontWeight: "700",
    color: "#C0392B",
  },
  sectionTitle: {
    fontFamily,
    fontSize: 16,
    fontWeight: "900",
    color: colors.ink,
    marginBottom: 12,
  },
  features: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.lg,
    padding: 16,
    marginBottom: 18,
  },
  featureRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 10,
  },
  check: {
    fontFamily,
    color: colors.primaryInk,
    fontSize: 15,
    fontWeight: "900",
    marginRight: 10,
    marginTop: 1,
  },
  feature: {
    flex: 1,
    fontFamily,
    fontSize: 14,
    fontWeight: "700",
    color: colors.ink,
  },
  note: {
    fontFamily,
    fontSize: 12,
    fontWeight: "600",
    color: colors.ink3,
    textAlign: "center",
    marginTop: 8,
  },
});
